<template>
  <v-row>
    <v-col cols="12" md="3">
      <v-card class="ml-n4" :loading="loading" flat>
        <v-skeleton-loader type="card-avatar, article, actions" v-if="loading"></v-skeleton-loader>
        <div v-else>
          <debug :debug="selection"></debug>
          <v-row dense>
            <v-col cols="6" sm="4" md="12">
              <v-list-item three-line>
                <v-list-item-content>
                  <v-list-item-subtitle>{{ $t("components.thg.ThgChargingStationCard.address") }}</v-list-item-subtitle>
                  <v-list-item-title> {{ selection.address.street }}</v-list-item-title>
                  <v-list-item-title> {{ selection.address.zip }} {{ selection.address.city }}</v-list-item-title>
                  <v-list-item-action-text v-if="hasGeo(selection)">
                    <a
                      :href="
                        'https://www.google.com/maps/search/?api=1&query=' +
                        selection.address.geo.lat +
                        '%2C' +
                        selection.address.geo.lng
                      "
                      target="_blank"
                    >
                      @{{ selection.address.geo.lat }},{{ selection.address.geo.lng }}
                    </a>
                  </v-list-item-action-text>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="6" sm="4" md="12">
              <v-list-item two-line v-if="selection.chargingPointOperator?.name">
                <v-list-item-content>
                  <v-list-item-subtitle>{{
                    $t("components.thg.ThgChargingStationCard.operator")
                  }}</v-list-item-subtitle>
                  <v-list-item-title>{{ selection.chargingPointOperator.name }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="6" sm="4" md="12" v-if="isBnetzA">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>{{ $t("components.thg.ThgChargingStationCard.bnetza") }}</v-list-item-subtitle>
                  <v-list-item-title>{{
                    selection.chargingPointOperator?.bnetzaOperatorId ||
                    $t("components.thg.ThgChargingStationCard.bnetzaUnkown")
                  }}</v-list-item-title>
                  <v-list-item-title>{{ registrationDate }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="6" sm="4" md="12">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>{{
                    $t("components.thg.ThgChargingStationCard.commissioningDate")
                  }}</v-list-item-subtitle>
                  <v-list-item-title>{{ commissioningDate }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="6" sm="4" md="12">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>{{
                    $t("components.thg.ThgChargingStationCard.connectionPowerInKwh")
                  }}</v-list-item-subtitle>
                  <v-list-item-title>{{ selection.connectionPowerInKwh }} kW</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="6" sm="4" md="12">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>{{
                    $t("components.thg.ThgChargingStationCard.chargingPointCount")
                  }}</v-list-item-subtitle>
                  <v-list-item-title>{{ selection.chargingPointCount || "1" }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>
            <v-col cols="6" sm="4" md="12" v-if="isEvseEnabled">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle>{{ $t("components.thg.ThgChargingStationCard.evseId") }}</v-list-item-subtitle>
                  <v-list-item-title v-for="(point, i) in selection.chargingPoints" :key="i">
                    {{ getEvseId(point.evseId) }}</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <!-- Is from renewable energy -->
            <v-col cols="6" sm="4" md="12" v-if="isRenewableChargingStationEnabled">
              <v-list-item two-line>
                <v-list-item-content>
                  <v-list-item-subtitle
                    >{{ $t("components.thg.ThgChargingStationCard.chargingStationRenewableEnergy") }}
                  </v-list-item-subtitle>
                  <v-list-item-title
                    >{{ isFromRenewableEnergy }}
                    <thg-charging-station-help-dialog
                      v-if="selection.isFromRenewableEnergy"
                      :helpTextTitle="$t('components.thg.ThgChargingStationForm.renewableEnergyDescription')"
                      :helpText="selection.renewableEnergySourceDescription"
                    ></thg-charging-station-help-dialog>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-col>

            <!-- Proof chip list -->
            <v-col cols="6" sm="4" md="12">
              <thg-charging-station-proof-chip-list
                :files="proofs"
                :refs="selection.refs"
                v-on:proof-update="onProofUpdate"
              ></thg-charging-station-proof-chip-list>
            </v-col>
          </v-row>
        </div>
      </v-card>
    </v-col>
    <v-col cols="12" md="9">
      <thg-charging-station-meter-reading-table
        :meterReadings="meterReadings"
        :selection="selection"
      ></thg-charging-station-meter-reading-table>
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { AssetEnum, AssetRepository } from "@/lib/AssetRepository";
import { IThgStatus } from "@/lib/interfaces/thg/thg-status.interface";
import { PartnerColor } from "@/lib/partnerColor";
import { handleError } from "@/lib/utility/handleError";
import { thgStatusMap } from "@/lib/utility/thgStatusMap";
import {
  ThgChargingStationProofDocumentDtoGen,
  ThgChargingStationViewModelGen,
  ThgThgMeterReadingViewModelGen,
  ThgUpdateMyChargingStationShortDtoGen
} from "@/services/thg/v1/data-contracts";
import { ChargingStationModule } from "@/store/modules/charging-station.store";
import { FeatureModule } from "@/store/modules/feature.store";
import { Component, Prop, Vue } from "vue-property-decorator";
import Debug from "../utility/Debug.vue";
import ThgChargingStationHelpDialog from "./ThgChargingStationHelpDialog.vue";
import ThgChargingStationMeterReadingTable from "./ThgChargingStationMeterReadingTable.vue";
import ThgChargingStationProofChipList from "./ThgChargingStationProofChipList.vue";

@Component({
  components: {
    Debug,
    ThgChargingStationMeterReadingTable,
    ThgChargingStationProofChipList,
    ThgChargingStationHelpDialog
  }
})
export default class ThgChargingStationCard extends Vue {
  @Prop()
  selection!: ThgChargingStationViewModelGen;

  @Prop()
  meterReadings!: ThgThgMeterReadingViewModelGen[];

  @Prop()
  loading!: boolean;

  partnerColor = new PartnerColor();
  thgStatusMap = thgStatusMap;

  get isBnetzA() {
    return this.selection.address.countryCode === "DE";
  }

  get isRenewableChargingStationEnabled() {
    return FeatureModule.isRenewableChargingStationEnabled;
  }

  get isFromRenewableEnergy() {
    return this.selection.isFromRenewableEnergy
      ? this.$t("components.thg.ThgChargingStationCard.yes")
      : this.$t("components.thg.ThgChargingStationCard.no");
  }

  get charging() {
    return AssetRepository.getAsset(this.$vuetify.theme.dark, AssetEnum.charging);
  }

  get dateOptions() {
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric"
    };
    return options;
  }

  get commissioningDate() {
    return this.toDate(this.selection.commissioningDate);
  }

  get proofs() {
    return this.selection.proofs;
  }

  get registrationDate(): string {
    if (!this.selection.registrationDate) {
      return "";
    }

    return this.toDate(this.selection.registrationDate);
  }

  get isEvseEnabled() {
    return FeatureModule.isEvseEnabled;
  }

  getEvseId(powerOutletId: string) {
    if (!this.selection.chargingPointOperator) {
      return "";
    }

    return `${this.selection.address.countryCode}*${this.selection.chargingPointOperator.evseOperatorId}*E${powerOutletId}`;
  }

  async onProofUpdate(files: ThgChargingStationProofDocumentDtoGen[]) {
    const refIds = this.selection?.refs?.map((ref) => ref.refId) || [];
    /** get ids of newly added files */
    const newFiles = files.filter((file) => !refIds.includes(file.id));

    /** Don't update charging station if there are no uploaded proofs(new ones) */
    if (newFiles.length === 0) {
      return;
    }

    const data: ThgUpdateMyChargingStationShortDtoGen = {
      refs: []
    };

    if (!data.refs) {
      return;
    }

    if (this.selection.refs) {
      data.refs.push(...this.selection.refs);
    }

    data.refs.push(
      ...newFiles.map((file) => ({
        refId: file.id,
        refType: file.extensionType
      }))
    );

    try {
      await ChargingStationModule.updateChargingStation({
        id: this.selection.id,
        data
      });
      this.$toast.success("👍");
    } catch (error) {
      handleError(error);
    }
  }

  addMeterReading() {
    this.$router.push({ path: `/charging-station/${this.selection.id}/meter-reading` });
  }

  toDate(date: string) {
    return new Date(date).toLocaleString("de-DE", this.dateOptions as any);
  }

  thgStatus(thgStatusEnum: any): IThgStatus {
    return (
      this.thgStatusMap.get(thgStatusEnum) || {
        text: "components.thg.ThgStatusMap.UNDEFINED.text",
        textAlt: "components.thg.ThgStatusMap.UNDEFINED.textAlt",
        icon: "mdi-help",
        portalText: "components.thg.ThgStatusMap.UNDEFINED.portalText",
        color: "error"
      }
    );
  }

  goToChargingStation(item: ThgChargingStationViewModelGen) {
    this.$router.push({ path: `/charging-station/${item.id}` });
  }

  hasGeo(selection: ThgChargingStationViewModelGen) {
    if (!selection.address.geo) {
      return false;
    }

    if (selection.address.geo.lat <= 0 && selection.address.geo.lng <= 0) {
      return false;
    }

    return true;
  }
}
</script>
